import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Modal, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getTeamStandings, GroupStanding, TeamStanding, editStandings, EditStandingsRequest, removeTeamFromStandings, RemoveTeamFromStandingsRequest, getTeamStanding } from '../../api/tournament/standingsService';
import { getAllAgeCategoriesPaginated } from '../../api/tournament/ageCategoryService';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TeamManagementCard from '../../Common/TeamManagementCard';
import TeamStandingsTable from '../../Common/TeamStandingsTable';
import { DeleteModal } from '../../Common/DeleteModal';
import '../../Common/TournamentHeader.css';
import '../../Common/ManageTournamentsFixturesTable.css';
import '../../components/DashboardStyles.css';
import { getImageUrl } from '../../utils/imageUrl';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import { hasTournamentWriteAccess } from '../../utils/roleBasedAccess';

const GroupDetailPage: React.FC = () => {
  const { tournamentId, ageCategoryId, groupName } = useParams<{ 
    tournamentId: string; 
    ageCategoryId: string; 
    groupName: string; 
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const canManageStandings = hasTournamentWriteAccess(
    { ...user, subRole: role, permissions },
    ['tournament.fixtures.manage']
  );
  
  const [groupStanding, setGroupStanding] = useState<GroupStanding | null>(null);
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [allGroupStandings, setAllGroupStandings] = useState<GroupStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Team management state
  const [showTeamManagementCard, setShowTeamManagementCard] = useState(false);
  const [addingTeams, setAddingTeams] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Edit team modal state
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamStanding | null>(null);
  const [editForm, setEditForm] = useState({
    played: '',
    won: '',
    lost: '',
    drawn: '',
    goalsFor: '',
    goalsAgainst: '',
    points: '',
    isQualified: false
  });
  const [editing, setEditing] = useState(false);

  // Qualified options for dropdown
  const qualifiedOptions = [
    { value: true, label: 'Qualified' },
    { value: false, label: 'Not Qualified' }
  ];
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<TeamStanding | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState(false);
  
  // Get age category info, view mode, tournamentSeasonId, and fromTournamentDetailPage flag from location state
  const [ageCategoryInfo, setAgeCategoryInfo] = useState(location.state?.ageCategoryInfo);
  const isViewMode = location.state?.viewMode || false;
  const tournamentSeasonId = location.state?.tournamentSeasonId; // Get tournamentSeasonId for filtering teams by season
  const fromTournamentDetailPage = location.state?.fromTournamentDetailPage || false; // Check if we came from tournament detail page (default season)

  // Debug logging
  console.log('GroupDetailPage - ageCategoryInfo:', ageCategoryInfo);
  console.log('GroupDetailPage - location.state:', location.state);

  const fetchAgeCategoryInfo = useCallback(async () => {
    try {
      console.log('GroupDetailPage - fetchAgeCategoryInfo called');
      console.log('GroupDetailPage - tournamentId:', tournamentId);
      console.log('GroupDetailPage - ageCategoryId:', ageCategoryId);
      
      // Get sport from location state or URL params
      const selectedTournamentFromState = (location.state as any)?.selectedTournament;
      const sportFromState = selectedTournamentFromState?.sports;
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      // Use shared utility - throws error if sport cannot be determined
      const sport = getSportValueUtil(null, location);
      
      // Fetch age category details using secureApi (properly handles token management)
      // Pass tournamentSeasonId when available (from location.state)
      const response = await getAllAgeCategoriesPaginated(tournamentId!, 1, 1000, '', '', '', tournamentSeasonId || undefined, sport);

      console.log('GroupDetailPage - API response:', response);
      
      // Handle different response structures
      let ageCategoriesData = null;
      if (response && response.paginatedResults) {
        ageCategoriesData = response.paginatedResults;
      } else if (response && response.data && response.data.paginatedResults) {
        ageCategoriesData = response.data.paginatedResults;
      } else if (Array.isArray(response)) {
        ageCategoriesData = response;
      }
      
      if (ageCategoriesData && ageCategoriesData.length > 0) {
        console.log('GroupDetailPage - ageCategories:', ageCategoriesData);
        const foundAgeCategory = ageCategoriesData.find((ac: any) => ac._id === ageCategoryId);
        console.log('GroupDetailPage - foundAgeCategory:', foundAgeCategory);
        
        if (foundAgeCategory) {
          const newAgeCategoryInfo = {
            _id: foundAgeCategory._id,
            ageCategory: foundAgeCategory.ageCategory,
            standingsType: foundAgeCategory.standingsType
          };
          console.log('GroupDetailPage - setting ageCategoryInfo:', newAgeCategoryInfo);
          setAgeCategoryInfo(newAgeCategoryInfo);
        }
      }
    } catch (error) {
      console.error('Error fetching age category info:', error);
    }
  }, [tournamentId, ageCategoryId]);

  useEffect(() => {
    console.log('GroupDetailPage - useEffect triggered');
    console.log('GroupDetailPage - tournamentId:', tournamentId);
    console.log('GroupDetailPage - ageCategoryId:', ageCategoryId);
    console.log('GroupDetailPage - groupName:', groupName);
    console.log('GroupDetailPage - ageCategoryInfo:', ageCategoryInfo);
    
    if (tournamentId && ageCategoryId && groupName) {
      loadGroupStandings();
      
      // If ageCategoryInfo is not provided, fetch it
      if (!ageCategoryInfo) {
        console.log('GroupDetailPage - ageCategoryInfo is undefined, calling fetchAgeCategoryInfo');
        fetchAgeCategoryInfo();
      } else {
        console.log('GroupDetailPage - ageCategoryInfo already exists, not fetching');
      }
    }
  }, [tournamentId, ageCategoryId, groupName, ageCategoryInfo]);

  // Set view mode from location state
  useEffect(() => {
    setViewMode(isViewMode);
  }, [isViewMode]);

  // Debug logging for standings
  useEffect(() => {
    console.log('GroupDetailPage - standings:', standings);
    console.log('GroupDetailPage - standings length:', standings.length);
    console.log('GroupDetailPage - groupStanding:', groupStanding);
    if (standings.length > 0) {
      console.log('GroupDetailPage - first standing:', standings[0]);
      console.log('GroupDetailPage - team names:', standings.map(s => s.team.name));
    }
  }, [standings, groupStanding]);

  const loadGroupStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const sport = getSportValueUtil(null, location);
      const seasonId = tournamentSeasonId || new URLSearchParams(location.search).get('seasonId') || undefined;
      const response = await getTeamStandings(ageCategoryId!, sport, seasonId);
      
      // Check if response exists and has the expected structure
      if (!response) {
        setError('No response received from server');
        return;
      }
      
      // The API returns the data directly as an array
      if (Array.isArray(response)) {
        // Handle empty data array
        if (response.length === 0) {
          setError('No group standings found');
          return;
        }
        
        // Check if it's group standings (has groupName property) or single standings
        const isGroupStandings = response[0].hasOwnProperty('groupName');
        
        if (isGroupStandings) {
          const groupStandings = response as GroupStanding[];
          // Filter out standings with null teams from all groups to prevent errors
          const cleanedGroupStandings = groupStandings.map(group => ({
            ...group,
            standings: (group.standings || []).filter(
              (standing: TeamStanding) => standing && standing.team && standing.team._id
            )
          }));
          // Store all group standings for filtering
          setAllGroupStandings(cleanedGroupStandings);
          
          // Find the specific group
          const decodedGroupName = decodeURIComponent(groupName!);
          const foundGroup = cleanedGroupStandings.find(group => group.groupName === decodedGroupName);
          
          if (foundGroup) {
            setGroupStanding(foundGroup);
            // Set the already-filtered standings
            setStandings(foundGroup.standings);
          } else {
            setError(`Group "${decodedGroupName}" not found`);
          }
        } else {
          setError('Expected group standings but received single standings');
        }
      } else {
        setError(`Invalid response format. Expected array but received: ${typeof response}`);
      }
    } catch (err: any) {
      console.error('Error loading group standings:', err);
      if (err.response) {
        setError(`API Error: ${err.response.status} - ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('Network Error: Unable to reach server');
      } else {
        setError(`Error: ${err.message || 'Failed to load group standings'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/manage-tournaments-fixtures/standings/group/${tournamentId}/${ageCategoryId}${location.search}`, {
      state: {
        ageCategoryInfo,
        tournamentSeasonId, // Pass tournamentSeasonId for back navigation
        fromTournamentDetailPage, // Pass fromTournamentDetailPage flag
        selectedTournament: (location.state as any)?.selectedTournament
      }
    });
  };

  const handleOpenEditTeamsCard = () => {
    setShowTeamManagementCard(true);
    setIsEditMode(true);
  };

  const handleEditTeam = async (team: TeamStanding) => {
    // Guard against null team
    if (!team || !team.team || !team.team._id) {
      toast.error('Invalid team data', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    try {
      const teamData = await getTeamStanding(
        ageCategoryId!,
        team.team._id,
        groupName,
        getSportValueUtil(null, location)
      );
      
      setEditForm({
        played: teamData.played?.toString() || '0',
        won: teamData.won?.toString() || '0',
        lost: teamData.lost?.toString() || '0',
        drawn: teamData.drawn?.toString() || '0',
        goalsFor: teamData.goalsFor?.toString() || '0',
        goalsAgainst: teamData.goalsAgainst?.toString() || '0',
        points: teamData.points?.toString() || '0',
        isQualified: teamData.isQualified || false
      });
      
      setEditingTeam(team);
      setShowEditTeamModal(true);
    } catch (err: any) {
      console.error('Error fetching team data:', err);
      toast.error('Failed to load team data for editing', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleEditTeamStandings = async () => {
    if (!editingTeam || !editingTeam.team || !editingTeam.team._id) {
      toast.error('Invalid team data', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    try {
      setEditing(true);
      
      const requestData: EditStandingsRequest = {
        categoryId: ageCategoryId!,
        teamId: editingTeam.team._id,
        updates: {
          played: parseInt(editForm.played) || 0,
          won: parseInt(editForm.won) || 0,
          lost: parseInt(editForm.lost) || 0,
          drawn: parseInt(editForm.drawn) || 0,
          goalsFor: parseInt(editForm.goalsFor) || 0,
          goalsAgainst: parseInt(editForm.goalsAgainst) || 0,
          points: parseInt(editForm.points) || 0,
          isQualified: editForm.isQualified,
        },
      };

      await editStandings(requestData, getSportValueUtil(null, location));
      
      // Refresh standings data
      await loadGroupStandings();
      
      // Show success message
      toast.success('Team standings updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close modal and reset state
      setShowEditTeamModal(false);
      setEditingTeam(null);
      
    } catch (err: any) {
      console.error('Error updating team standings:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update team standings';
      toast.error(`Error: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setEditing(false);
    }
  };

  const handleRemoveTeam = async (team: TeamStanding) => {
    // Guard against null team
    if (!team || !team.team || !team.team._id) {
      toast.error('Invalid team data', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setTeamToDelete(team);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!teamToDelete || !teamToDelete.team || !teamToDelete.team._id) {
      toast.error('Invalid team data', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      setDeleting(true);
      
      const requestData: RemoveTeamFromStandingsRequest = {
        tournamentId: tournamentId!,
        ageCategoryId: ageCategoryId!,
        teamId: teamToDelete.team._id,
        groupName: groupName,
        groupId: groupStanding?.groupId,
      };

      await removeTeamFromStandings(requestData, getSportValueUtil(null, location));
      
      // Refresh standings data
      await loadGroupStandings();
      
      // Show success message
      toast.success(`${teamToDelete.team?.name || 'Team'} has been removed from Group ${decodeURIComponent(groupName!)}.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setTeamToDelete(null);
      
    } catch (err: any) {
      console.error('Error removing team:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove team';
      toast.error(`Error removing team: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleStandingsUpdate = async () => {
    await loadGroupStandings();
    setShowTeamManagementCard(false);
    setIsEditMode(false);
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" rows={10} />
        </Container>
      </div>
    );
  }

  const decodedGroupName = decodeURIComponent(groupName!);

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments-fixtures') },
              { 
                label: 'Tournament Details', 
                onClick: () => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: { activeTab: 'standings' } 
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: { activeTab: 'standings' } 
                    });
                  }
                }
              },
              { 
                label: `${ageCategoryInfo?.ageCategory || 'Age Category'} - Group Standings`, 
                onClick: handleBack
              },
              { label: `Group ${decodedGroupName}`, active: true }
            ]}
          />
        </div>

        {/* Header - Match TournamentDetailPage styling */}
        <div style={{ padding: '0 15px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={handleBack}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="tournament-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
                <div className="default-tournament-logo-small">
                  <i className="ri-group-line"></i>
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>
                  Group {decodedGroupName} - Teams
                </h1>
                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                  <span className="tournament-short-name-badge">Group</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '0 15px' }}>
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ri-group-line me-2"></i>
                Team Standings
              </h5>
              <Button
                variant="primary"
                onClick={handleOpenEditTeamsCard}
                disabled={viewMode || !canManageStandings}
                className="d-flex align-items-center"
              >
                <i className={standings.length > 0 ? "ri-edit-line me-2" : "ri-add-line me-2"}></i>
                {standings.length > 0 ? 'Edit Teams' : 'Add Teams'}
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Error Message */}
              {error && (
                <div className="alert alert-danger mb-3">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </div>
              )}

              {/* Add/Edit Teams Card - Above Table */}
              {groupStanding && (
                <TeamManagementCard
                  tournamentId={tournamentId!}
                  ageCategoryId={ageCategoryId!}
                  ageCategoryName={ageCategoryInfo?.ageCategory}
                  standings={standings}
                  allGroupStandings={allGroupStandings.map(group => group.standings)}
                  onStandingsUpdate={handleStandingsUpdate}
                  groupName={decodedGroupName}
                  contextTitle={`Group ${decodedGroupName} Teams`}
                  tournamentSeasonId={tournamentSeasonId} // Pass tournamentSeasonId to filter teams by season
                  showCard={showTeamManagementCard}
                  onCloseCard={() => {
                    setShowTeamManagementCard(false);
                    setIsEditMode(false);
                  }}
                  isEditMode={isEditMode}
                  onSetEditMode={setIsEditMode}
                  addingTeams={addingTeams}
                  onSetAddingTeams={setAddingTeams}
                  showGroupNameInput={false}
                />
              )}

              {/* Team Standings Table */}
              <TeamStandingsTable
                standings={standings}
                viewMode={viewMode || !canManageStandings}
                onEditTeam={handleEditTeam}
                onRemoveTeam={handleRemoveTeam}
              />
            </Card.Body>
          </Card>
        </div>

        {/* Edit Team Modal */}
      <Modal show={showEditTeamModal} onHide={() => setShowEditTeamModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-edit-line me-2 text-primary"></i>
            Edit Team Standings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTeam && editingTeam.team && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                {editingTeam.team.logo ? (
                  <img
                    src={getImageUrl(editingTeam.team.logo)}
                    alt={editingTeam.team.name || 'Team logo'}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.setAttribute('style', 'display: flex');
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '50%',
                    display: editingTeam.team.logo ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6c757d'
                  }}
                >
                  {editingTeam.team.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="ms-3">
                  <h6 className="mb-0 fw-semibold">{editingTeam.team.name || 'Unknown Team'}</h6>
                  <small className="text-muted">{editingTeam.team.shortName || ''}</small>
                </div>
              </div>
            </div>
          )}
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Matches Played</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.played}
                  onChange={(e) => setEditForm({ ...editForm, played: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Wins</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.won}
                  onChange={(e) => setEditForm({ ...editForm, won: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Losses</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.lost}
                  onChange={(e) => setEditForm({ ...editForm, lost: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Draws</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.drawn}
                  onChange={(e) => setEditForm({ ...editForm, drawn: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Goals For</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.goalsFor}
                  onChange={(e) => setEditForm({ ...editForm, goalsFor: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Goals Against</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.goalsAgainst}
                  onChange={(e) => setEditForm({ ...editForm, goalsAgainst: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Points</Form.Label>
                <Form.Control
                  type="number"
                  value={editForm.points}
                  onChange={(e) => setEditForm({ ...editForm, points: e.target.value })}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Qualified Status</Form.Label>
                <Select
                  value={qualifiedOptions.find(option => option.value === editForm.isQualified)}
                  onChange={(selectedOption) => setEditForm({ ...editForm, isQualified: selectedOption?.value || false })}
                  options={qualifiedOptions}
                  isSearchable={false}
                  placeholder="Select qualification status"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditTeamModal(false)} disabled={editing}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditTeamStandings} disabled={editing}>
            {editing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="ri-save-line me-2"></i>
                Update Standings
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={handleConfirmDelete}
        title={`Remove Team from Group ${decodedGroupName}`}
        message={`Are you sure you want to remove ${teamToDelete?.team?.name || 'this team'} from Group ${decodedGroupName}? This action cannot be undone.`}
        confirmLabel="Yes, Remove"
        cancelLabel="Cancel"
        isLoading={deleting}
      />
    </Container>
    </div>
  );
};

export default GroupDetailPage;
